import { readingBody, lessonContentVersion } from '@mio/shared';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, ChevronDown, Clock3 } from 'lucide-react';

import { Button } from '../button';

import { ActivityChecks } from './activity-checks';
import { ACTIVITY_KIND, activityHref, formatMinutes } from './activity-kind';
import { outlineOf } from './outline';
import { ReadingDocument } from './reading-document';

import type { ActivityView } from '../../program/derive';

export interface ActivityReaderProps {
  activity: ActivityView;
  onComplete?: () => void;
  completionNote?: string;
  completing: boolean;
  /** Label for the primary action once the activity is complete-able. */
  completeLabel?: string;
}

/**
 * The reading surface for any activity: one calm column, the body at full contrast,
 * visual explanations, worked examples, and reading progress at the end.
 */
export function ActivityReader({
  activity,
  onComplete,
  completing,
  completeLabel = 'Mark complete',
  completionNote,
}: ActivityReaderProps) {
  const kind = ACTIVITY_KIND[activity.kind];
  const minutes = formatMinutes(activity.estimatedMinutes);
  const done = activity.status === 'complete';
  const outline =
    activity.kind === 'lesson' && !activity.body.startsWith('```reading-sections')
      ? outlineOf(activity.body)
      : [];
  const visual = /^```learning-(?:steps|lab)\s*$/m.test(activity.body);

  return (
    <article
      className="lesson-reader mx-auto max-w-[760px] py-4 sm:py-8"
      {...(activity.kind === 'lesson'
        ? {
            'data-lesson-id': activity.id,
            'data-lesson-version': lessonContentVersion(activity.body),
          }
        : {})}
    >
      <div className="text-fg-muted flex flex-wrap items-center justify-between gap-3 text-xs">
        <p className="flex flex-wrap items-center gap-x-2">
          <span>Module {activity.module.number}</span>
          <span aria-hidden="true">·</span>
          <span>{kind.label}</span>
          {minutes ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="size-3" /> {minutes}
              </span>
            </>
          ) : null}
        </p>
      </div>

      <header className="mt-3 sm:mt-5">
        <h1 className="text-fg text-[25px] font-semibold leading-[1.25] tracking-[-0.015em] sm:text-[28px]">
          {activity.title}
        </h1>
        {!visual ? (
          <p className="text-fg-muted mt-2 text-[15px] leading-6 sm:mt-3 sm:leading-7">
            {activity.summary}
          </p>
        ) : null}
        {activity.capabilityDetails.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-2 sm:mt-4" aria-label="Capabilities">
            {activity.capabilityDetails.map((c) => (
              <li key={c.slug} className="text-fg-muted bg-surface rounded px-2.5 py-1 text-[12px]">
                {c.title}
                {c.isEdge ? <span className="text-fg ml-1.5 font-medium">edge</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </header>

      <div
        id="module-content"
        className="border-line mt-4 scroll-mt-6 border-t pt-4 sm:mt-8 sm:pt-8"
      >
        {outline.length > 2 ? (
          <details className="border-line mb-9 border-b pb-5">
            <summary className="text-fg-muted flex list-none items-center justify-between text-xs">
              <span>
                Inside this lesson{' '}
                <span className="text-fg-subtle ml-2">{outline.length} sections</span>
              </span>
              <ChevronDown className="size-4" />
            </summary>
            <nav aria-label="On this page">
              <ol className="mt-4 columns-1 gap-x-8 text-sm leading-8 sm:columns-2">
                {outline.map((entry, index) => (
                  <li key={entry.id} className="break-inside-avoid">
                    <a href={`#${entry.id}`} className="text-fg hover:underline">
                      <span className="text-fg-subtle mr-2 tabular-nums">{index + 1}</span>
                      {entry.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </details>
        ) : null}
        <ReadingDocument source={activity.body} />
        {activity.kind === 'lesson' && visual ? (
          <ActivityChecks key={activity.id} checks={activity.checks} />
        ) : null}
        {activity.kind === 'workshop' && activity.acceptance.length > 0 ? (
          <section
            className="border-line bg-surface mt-10 rounded-md border p-5"
            aria-labelledby="acceptance-title"
          >
            <h2 id="acceptance-title" className="text-fg text-sm font-medium">
              Done when
            </h2>
            <ul className="mt-3 space-y-2 text-[15px] leading-7">
              {activity.acceptance.map((item) => (
                <li key={item} className="flex gap-3">
                  <Check
                    className="text-fg-muted mt-1.5 size-4 shrink-0"
                    strokeWidth={1.6}
                    aria-hidden="true"
                  />
                  <span className="text-fg">{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <footer className="border-line mt-12 border-t pt-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {done ? (
            <span className="text-success inline-flex items-center gap-2 text-[14px] font-medium">
              <Check className="size-4" strokeWidth={2} aria-hidden="true" /> Complete
            </span>
          ) : onComplete ? (
            <Button onClick={onComplete} loading={completing}>
              {completeLabel}
            </Button>
          ) : completionNote ? (
            <span className="text-fg-muted text-sm">{completionNote}</span>
          ) : null}
          {activity.next ? (
            <Link
              to={activityHref(activity.next.kind, activity.next.id)}
              className="text-fg inline-flex min-w-0 items-center gap-2 text-[14px] font-semibold [overflow-wrap:anywhere] hover:underline"
            >
              Next: {activity.next.title}
              <ArrowRight className="size-4" strokeWidth={1.6} aria-hidden="true" />
            </Link>
          ) : null}
        </div>
        {activity.previous ? (
          <Link
            to={activityHref(activity.previous.kind, activity.previous.id)}
            className="text-fg-muted hover:text-fg mt-4 inline-flex items-center gap-2 text-xs"
          >
            <ArrowLeft className="size-3.5" strokeWidth={1.6} aria-hidden="true" /> Previous:{' '}
            {activity.previous.title}
          </Link>
        ) : null}
        <details className="text-fg-muted mt-6 text-xs">
          <summary className="w-fit cursor-pointer rounded py-2">View lesson Markdown</summary>
          <pre
            className="text-fg mt-3 whitespace-pre-wrap break-words font-mono text-[13px] leading-6"
            aria-label="Lesson Markdown source"
          >
            {readingBody(activity.body)}
          </pre>
        </details>
      </footer>
    </article>
  );
}
